import { Thermometer, Flame, Sun, Home, Zap, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";

const iconMap = {
  thermometer: Thermometer,
  flame: Flame,
  sun: Sun,
  home: Home,
  zap: Zap,
  droplets: Droplets,
};

const serviceImages = {
  clima: "https://images.unsplash.com/photo-1613620838744-a0fbfb76bae3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxodmFjJTIwYWMlMjBpbnN0YWxsYXRpb24lMjB3b3JrZXJ8ZW58MHx8fHwxNzc1ODMzNDM4fDA&ixlib=rb-4.1.0&q=85",
  caldaia: "https://images.unsplash.com/photo-1676210133055-eab6ef033ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxib2lsZXIlMjBpbnN0YWxsYXRpb24lMjBnYXMlMjBoZWF0aW5nJTIwdGVjaG5pY2lhbnxlbnwwfHx8fDE3NzYwOTAzOTl8MA&ixlib=rb-4.1.0&q=85",
  fotovoltaico: "https://images.unsplash.com/photo-1761158495585-eac721decf1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzb2xhciUyMHBhbmVscyUyMG9uJTIwcm9vZiUyMGhvbWV8ZW58MHx8fHwxNzc1ODMzNDM4fDA&ixlib=rb-4.1.0&q=85",
  tetti: "https://images.pexels.com/photos/31771165/pexels-photo-31771165.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  elettrico: "https://images.pexels.com/photos/9679179/pexels-photo-9679179.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  idraulico: "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=600",
};

const ServicesSection = ({ services }) => {
  const handleScroll = (href) => {
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <section id="servizi" className="py-24 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#5abb3d] font-semibold tracking-[0.2em] uppercase text-sm">
            I Nostri Servizi
          </span>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-[#29383d] mt-4 tracking-tight">
            Soluzioni Complete per la Tua Casa
          </h2>
          <p className="text-[#394b52] mt-4 max-w-2xl mx-auto leading-relaxed">
            Offriamo una gamma completa di servizi per il comfort e l'efficienza energetica della tua abitazione
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Zap;
            const imageUrl = serviceImages[service.id] || serviceImages.clima;
            
            return (
              <div
                key={service.id}
                className={`service-card group relative overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white opacity-0 animate-fade-in-up stagger-${index + 1} ${
                  index === 0 ? "lg:col-span-2 lg:row-span-2" : ""
                }`}
                data-testid={`service-card-${service.id}`}
              >
                {/* Image */}
                <div className={`relative ${index === 0 ? "h-64 lg:h-full" : "h-48"} overflow-hidden`}>
                  <img
                    src={imageUrl}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#29383d]/80 to-transparent" />
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 left-4 w-12 h-12 bg-[#5abb3d] rounded-xl flex items-center justify-center shadow-lg">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-outfit text-xl font-bold mb-2">{service.title}</h3>
                  <p className={`text-white/80 text-sm leading-relaxed ${index === 0 ? "" : "line-clamp-2"}`}>
                    {service.description}
                  </p>
                  {index === 0 && (
                    <Button
                      onClick={() => handleScroll("#preventivo")}
                      className="mt-4 bg-[#5abb3d] hover:bg-[#4a9a32] text-white rounded-full px-6"
                      data-testid="service-cta-btn"
                    >
                      Richiedi Info
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Emergency Banner */}
        <div className="mt-12 bg-[#29383d] rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="emergency-banner">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-[#5abb3d] rounded-full flex items-center justify-center animate-pulse-green">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="font-outfit text-xl font-bold text-white">Pronto Intervento 24/7</h3>
              <p className="text-white/70">Emergenze elettriche e idrauliche? Siamo sempre disponibili!</p>
            </div>
          </div>
          <a
            href="tel:+393292656988"
            className="flex items-center gap-2 bg-[#5abb3d] hover:bg-[#4a9a32] text-white rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-1 hover:shadow-lg"
            data-testid="emergency-call-btn"
          >
            Chiama: 329 265 6988
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
